import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalAnalyticsMapperUtil, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ApplicationPathConstants } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { AuthenticationManageService } from '@ddt-web/core';
import { ApplicationConstants, HomePageModelFamily, MiniCompareUtil, ModelListRow, ModelRowData, ModelsBrowseDataService, ModelsBrowseFilterTransformUtil, ModelsBrowseStoreConfig, ModelsExperienceFilters } from '@ddt-web/models-browsing';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import * as isEmpty from 'lodash/isEmpty';
import * as uniqBy from 'lodash/uniqBy';
import { Observable, of } from 'rxjs';
import { first, map } from 'rxjs/operators';

@Component({
	selector: 'me-models-home',
	templateUrl: './models-home.component.html',
	styleUrls: ['./models-home.component.scss']
})
export class ModelsHomeComponent implements OnInit {
	@InjectConfig(ModelsExperienceConfigKey.HOME_PAGE_SELECTED_CARD_FAMILIES_PER_CATEGORY)
	private readonly families_per_category_data: HomePageModelFamily = {};


	public selectedExploreCard: string;
	public familyIds$: Observable<string[]>;
	public families: string[] = [];
	public modelFilter$: Observable<ModelsExperienceFilters[]>;
	public familyModelsPerCategory: { [categoryId: string]: { [familyId: string]: string[] } } = {};
	public selectedModels: Array<ModelRowData> = [];
	public selectedModelIds: Array<string>;
	public isBlkModelsAvailable$: Observable<boolean>;
	private selectedLeftNav: string;
	private isAuthenticated: boolean;

	constructor(
		private storeService: StoreService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private router: Router,
		private authManagerService: AuthenticationManageService,
		private modelsBrowseDataService: ModelsBrowseDataService
	) { }

	public ngOnInit(): void {
		GlobalAnalyticsMapperUtil.setPageName(TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_HOME_PAGE);
		this.authManagerService.getUserAuthenticated().pipe(first()).subscribe((authenticated) => {
			this.isAuthenticated = authenticated;
			if (!this.isAuthenticated) {
				this.router.navigate([], { queryParams: { triggerSignIn: true }, queryParamsHandling: 'merge' });
			}
		});

		this.tealiumAnalyticsService.triggerToolStart(TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE);
		this.isBlkModelsAvailable$ = this.modelsBrowseDataService.isBlkModelsAvailable();
	}

	public setSelectedGreyCardsDetail(selectedLeftNav: string): void {
		this.selectedLeftNav = selectedLeftNav;
		const selectedGreyCardsDetail = this.families_per_category_data[this.selectedExploreCard][this.selectedLeftNav];
		this.families = selectedGreyCardsDetail.filter(item => !!item.familyId).map(item => item.familyId);
		this.familyIds$ = of([...this.families]);
		this.modelFilter$ = this.storeService.selectAll(ModelsBrowseStoreConfig.filterConfig).pipe(
			map((filtersList) => Object.values(filtersList?.entities ?? {}).filter(item => item.id === this.selectedExploreCard)),
			map(element => [...element, ...ModelsBrowseFilterTransformUtil.getFilterList([this.selectedLeftNav])])
		);
	}

	public setSelectedModelPortfolios(data: ModelListRow): void {
		const flattenedData = MiniCompareUtil.flattenSelectedModelsDataFromStore([data]);
		this.selectedModels = data.checked ? [...this.selectedModels, ...flattenedData] :
			this.selectedModels.filter(item => item?.modelId !== flattenedData?.[0]?.modelId);
	}

	public onOpenMiniCompareDialog(): void {
		const userFilterQueryParams = this.getFilterQueryParam();
		const selectedModelsId = this.selectedModels.map((list) => list?.modelId);
		this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(`mini-compare-bar:button click:${selectedModelsId}`, {
			toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE
		}));
		this.applicationStateStoreProxyService.updateMeSelectedModelsData({ routedFrom: ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.HOME, ...this.selectedModels });
		const selectedModelsParam = ModelsBrowseFilterTransformUtil.getSelectedModelsQueryParam(this.selectedModels);
		const params = {
			'userFilterName': !isEmpty(userFilterQueryParams) ? userFilterQueryParams.join('$') : null,
			'allRequestParams': !isEmpty(selectedModelsParam) ? selectedModelsParam.join('$') : null,
			'routedFrom': ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.HOME
		};

		if (!this.isAuthenticated) {
			this.authManagerService.initializeOktaSignIn({ 'redirectUrlPath': '/' + ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FIND_AND_COMPARE, ...params });
		} else {
			this.router.navigate([ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FIND_AND_COMPARE], { queryParams: params, queryParamsHandling: 'merge' });
		}
	}

	public onRemoveSelectedModelsData(selectedModels: Array<any>): void {
		this.selectedModels = selectedModels;
	}

	private getFilterQueryParam(): Array<string> {
		const selectedFilters: Array<ModelsExperienceFilters> = [];
		const highlightedKeys = uniqBy(this.selectedModels, 'exploreCardType').map(data => data?.exploreCardType);
		const dropdownKeys = uniqBy(this.selectedModels, 'leftNav').map(data => data?.leftNav);
		const highlightedFilters = ModelsBrowseFilterTransformUtil.getFilterList(highlightedKeys, ApplicationConstants.HIGHLIGHTED);
		selectedFilters.push(...highlightedFilters);
		const dropdownFilters = ModelsBrowseFilterTransformUtil.getFilterList(dropdownKeys);
		selectedFilters.push(...dropdownFilters);
		return ModelsBrowseFilterTransformUtil.getFilterQueryParam(selectedFilters);
	}
}
